import React, { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import LoyaltyUserSummary from 'components/loyalty/userSummary';
import { logout } from 'components/login/actions';
import { hideCart } from 'components/cart/actions';
import CategoryLink from 'components/categoryLink';
import { MutedBackground } from 'scy/Background';
import { block } from 'scy/display.mod.scss';
import padding from 'scy/padding.mod.scss';
import margin from 'scy/margin.mod.scss';
import { LightHR } from 'scy/HR';
import { H5, AccentH5 } from 'scy/text';
import { accent } from 'scy/text/styles.mod.scss';
import { TextButton } from 'scy/buttons';
import { slideoutMenu, slideoutPanelOpen } from './styles.mod.scss';
import UserIcon from 'images/icons/user.svg?react-svg';
import WordmarkIcon from 'images/icons/logos/wordmark.svg?react-svg';

export default function SideNav({ panelRef, toggleRef }) {
  const dispatch = useDispatch();

  const loyalty = useSelector(state => state.loyalty);
  const user = useSelector(state => state.login.user);

  const slideout = useRef();

  const menuRef = useRef();

  useEffect(() => {
    if (!window.Slideout) return;

    const panel = panelRef.current;
    const menu = menuRef.current;
    const toggle = toggleRef.current;

    if (!panel || !menu || !toggle) return;

    function menuSize() {
      return document.body.offsetWidth - 60;
    }

    slideout.current = new window.Slideout({
      panel,
      menu,
      side: 'left',
      padding: menuSize()
    });

    // See https://github.com/Mango/slideout/issues/219
    function handleResize() {
      const padding = menuSize();
      slideout.current._padding = padding;
      slideout.current._translateTo = slideout.current._orientation * padding;
      if (slideout.current.isOpen()) {
        slideout.current._translateXTo(slideout.current._translateTo);
      }
      menu.style.width = padding + 'px';
    }

    function handleToggle() {
      return slideout.current.toggle();
    }

    function handleBeforeOpen() {
      return panel.classList.add(slideoutPanelOpen);
    }

    function handleBeforeClose() {
      return panel.classList.remove(slideoutPanelOpen);
    }

    window.addEventListener('resize', handleResize);
    toggle.addEventListener('click', handleToggle);
    slideout.current.on('beforeopen', handleBeforeOpen);
    slideout.current.on('beforeclose', handleBeforeClose);

    return function cleanupSideNav() {
      window.removeEventListener('resize', handleResize);
      toggle.removeEventListener('click', handleToggle);
      slideout.current.off('beforeopen', handleBeforeOpen);
      slideout.current.off('beforeclose', handleBeforeClose);
      slideout.current = null;
    };
  }, [menuRef, panelRef, toggleRef]);

  function handleNavigation() {
    dispatch(hideCart());
    if (slideout.current && typeof slideout.current.close === 'function') {
      slideout.current.close();
    }
  }

  return (
    <MutedBackground tag="nav" ref={menuRef} className={slideoutMenu}>
      {!user ? (
        <section>
          <NavLink href="/signup">
            <UserIcon width={11} height={11} className={margin.right_xs} />
            Sign up
          </NavLink>
          <NavLink href="/login">Login</NavLink>
        </section>
      ) : (
        <section className={`${padding.top_md} ${padding.bottom_xxs}`}>
          <Link to="/loyalty" onClick={handleNavigation}>
            <LoyaltyUserSummary
              currentPoints={loyalty.currentPoints}
              currentStatus={loyalty.currentStatus}
              nextStatus={loyalty.nextStatus}
              user={user}
              loading={loyalty.loading}
            />
          </Link>
        </section>
      )}

      <Divider />

      <section>
        <CategoryNavLink tag="BE" onClick={handleNavigation}>
          Beer
        </CategoryNavLink>
        <CategoryNavLink tag="WN" onClick={handleNavigation}>
          Wine
        </CategoryNavLink>
        <CategoryNavLink tag="ST" onClick={handleNavigation}>
          Spirits
        </CategoryNavLink>
        <CategoryNavLink tag="MX" onClick={handleNavigation}>
          Mixers
        </CategoryNavLink>
        <CategoryNavLink tag="OT" url="/more" onClick={handleNavigation}>
          More
        </CategoryNavLink>
        <AccentH5 uppercase tag="div">
          <Link
            to="/gifting"
            onClick={handleNavigation}
            className={`${block} ${padding.sm}`}
          >
            Gifts
          </Link>
        </AccentH5>
      </section>

      <Divider />

      <section>
        {!!user && <NavLink href="/share">Invite Friends</NavLink>}
        {!!user && <NavLink href="/account">Your Account</NavLink>}
        {!!user && !!user.orders && !!user.orders.length && (
          <NavLink href="/orders">Your Orders</NavLink>
        )}
        <NavLink href="/blog">Blog</NavLink>
        <NavLink
          target="_blank"
          rel="noreferrer noopener"
          href="http://help.sauceyapp.com/"
        >
          FAQs
        </NavLink>
        {!!user && (
          <H5 uppercase tag="div" className={padding.sm}>
            <TextButton noUnderline onClick={() => dispatch(logout())}>
              Logout
            </TextButton>
          </H5>
        )}
      </section>

      <Divider />

      <div className={`${padding.sm} ${accent}`}>
        <Link to="/">
          <WordmarkIcon width={120} />
        </Link>
      </div>
    </MutedBackground>
  );
}

function NavLink({ children, ...props }) {
  return (
    <AccentH5 uppercase className={`${block} ${padding.sm}`} tag="a" {...props}>
      {children}
    </AccentH5>
  );
}

function CategoryNavLink({ tag, url, onClick, children }) {
  return (
    <AccentH5 uppercase tag="div">
      <CategoryLink
        tag={tag}
        url={url}
        onClick={onClick}
        className={`${block} ${padding.sm}`}
      >
        {children}
      </CategoryLink>
    </AccentH5>
  );
}

function Divider() {
  return <LightHR className={`${margin.left_xs} ${margin.right_xs}`} />;
}
